import type { BulkAction, BulkActionBody, BulkActionResponse, LeadRow } from "@gioia365/contracts";
import type { Locale } from "@gioia365/i18n";
import { formatCount, italianCountArticle } from "../../i18n/format";
import type { Translate } from "../../i18n/locale";
import { formatPhoneForDisplay, leadName } from "../lead-display";

// Le parole delle «Azioni multiple» che dipendono da un numero (piano M3 Task 14, decisione P41): l'articolo italiano
// davanti al numero («tutti gli 11», «tutti i 18»), il singolare con un Lead solo («l'unico risultato», «1 Lead» anche
// in inglese) e i numeri con il separatore delle migliaia.

/** Quanti nomi dice il riepilogo prima di «e altri N» (mock-up). */
const NAMED = 3;

/**
 * Il nome di un Lead nella casella e nel riepilogo: quello della tabella (nome e cognome, oppure il telefono
 * principale), poi un altro telefono o il salone; «—» soltanto senza nulla.
 */
export function bulkLeadName(row: Pick<LeadRow, "displayName" | "primaryPhone" | "mobile" | "landline" | "salonName">): string {
  const phone = row.mobile ?? row.landline;
  return leadName(row) ?? (phone ? formatPhoneForDisplay(phone) : null) ?? (row.salonName?.trim() || "—");
}

/** «Seleziona tutti i N risultati», con l'articolo giusto, oppure «Seleziona l'unico risultato». */
export function selectAllText(t: Translate, locale: Locale, count: number): string {
  const article = italianCountArticle(count);
  if (article === "one") return t("bulkActions.selectAllOne");
  return t(article === "gli" ? "bulkActions.selectAllGli" : "bulkActions.selectAll", { count: formatCount(locale, count) });
}

/** «Riguarda tutti i N Lead della vista di adesso…», con l'articolo giusto, oppure «Riguarda l'unico Lead…». */
export function allResultsText(t: Translate, locale: Locale, count: number): string {
  const article = italianCountArticle(count);
  if (article === "one") return t("bulkActions.allResultsOne");
  return t(article === "gli" ? "bulkActions.allResultsGli" : "bulkActions.allResults", { count: formatCount(locale, count) });
}

/** «Riguarda N Lead: A, B, C e altri K.» per i Lead spuntati, nell'ordine in cui li si è spuntati. */
export function summaryText(t: Translate, locale: Locale, names: readonly string[]): string {
  const shown = names.slice(0, NAMED).join(", ");
  const others = names.length - NAMED;
  const listed = others > 0 ? `${shown} ${t("bulkActions.andOthers", { count: formatCount(locale, others) })}` : shown;
  return names.length === 1 ? t("bulkActions.summaryOne", { names: listed }) : t("bulkActions.summary", { count: formatCount(locale, names.length), names: listed });
}

/** Il pulsante che conferma, con il numero dei Lead («Cambia status a 312 Lead»). */
export function confirmText(t: Translate, locale: Locale, action: BulkAction, count: number): string {
  return count === 1 ? t(`bulkActions.confirmOne.${action}`) : t(`bulkActions.confirm.${action}`, { count: formatCount(locale, count) });
}

/** «Crea il tag «…» e aggiungilo a N Lead»: la voce di «Aggiungi tag» che crea il tag e scrive subito sul gruppo. */
export function createAndAddText(t: Translate, locale: Locale, name: string, count: number): string {
  return count === 1 ? t("bulkActions.createTagOne", { name }) : t("bulkActions.createTag", { name, count: formatCount(locale, count) });
}

/** Una voce di «Togli tag»: il tag e quanti dei Lead spuntati ce l'hanno. */
export function tagCountText(t: Translate, locale: Locale, tag: string, count: number, total: number): string {
  return count === 1
    ? t("bulkActions.tagCountOne", { tag, total: formatCount(locale, total) })
    : t("bulkActions.tagCount", { tag, count: formatCount(locale, count), total: formatCount(locale, total) });
}

/**
 * L'esito in parole («Fatto: cambio status su 312 Lead.»), con quanti erano già così e quanti non si potevano cambiare.
 * Con «tutti i risultati» il server lavora sul gruppo di adesso: se è diverso dal numero visto lo si dice. Quando chi
 * agisce vede più Lead di quanti ne modifica (`leftOutPossible`), il server lascia fuori gli altri senza contarli a parte
 * (Task 13): un gruppo più piccolo può voler dire anche questo, e la frase dice tutte e due le cause.
 */
export function bulkOutcomeText(t: Translate, locale: Locale, input: { body: BulkActionBody; response: BulkActionResponse; leftOutPossible: boolean }): string {
  const { body, response, leftOutPossible } = input;
  const label = t(`bulkActions.labels.${body.action}`);
  let text = response.affected === 1 ? t("bulkActions.doneOne", { label }) : t("bulkActions.done", { label, affected: formatCount(locale, response.affected) });
  if (response.unchanged === 1) text += t("bulkActions.doneUnchangedOne");
  else if (response.unchanged > 1) text += t("bulkActions.doneUnchanged", { unchanged: formatCount(locale, response.unchanged) });
  const skipped = response.skipped.length;
  if (skipped === 1) text += t("bulkActions.doneSkippedOne");
  else if (skipped > 1) text += t("bulkActions.doneSkipped", { skipped: formatCount(locale, skipped) });
  if (body.target.kind === "query" && response.candidates !== body.target.expectedTotal) {
    const expected = body.target.expectedTotal;
    const missing = expected - response.candidates;
    let different: string;
    if (response.candidates < expected && leftOutPossible) {
      different = missing === 1 ? t("bulkActions.doneLeftOutOne") : t("bulkActions.doneLeftOut", { count: formatCount(locale, missing) });
    } else {
      different = t("bulkActions.doneDifferent", { candidates: formatCount(locale, response.candidates), expected: formatCount(locale, expected) });
    }
    text = `${text} ${different}`;
  }
  return text;
}
